import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTriageList,
  getTriageItem,
  getTriageAttachmentViewUrl,
} from 'services/escriturasTriageService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { useSnackbar } from 'context/SnackbarContext';
import { escriturasTriageI18n } from './i18n';
import TriageResolveDispatchModal from './TriageResolveDispatchModal';
import TriageAssignCaseModal from './TriageAssignCaseModal';
import TriageDismissModal from './TriageDismissModal';
import { formatDateTime } from './format';
import '../../styles/naEeppTableGrid.scss';

const STATUS_VALUES = [
  'pending',
  'resolved_dispatch',
  'resolved_case',
  'dismissed',
  'all',
];

function FilePreviewModal({ Modal, fileUrl, fileName, onClose }) {
  if (!Modal) {
    return (
      <div
        role='dialog'
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.55)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ background: '#fff', borderRadius: 8, padding: 16 }}
        >
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <strong>{fileName}</strong>
            <button type='button' onClick={onClose}>
              ×
            </button>
          </div>
          <iframe
            src={fileUrl}
            title={fileName}
            style={{ width: '70vw', height: '70vh', border: 0 }}
          />
        </div>
      </div>
    );
  }
  return (
    <Modal title={fileName} show onClose={onClose} size='lg'>
      <iframe
        src={fileUrl}
        title={fileName}
        style={{ width: '100%', height: '70vh', border: 0 }}
      />
    </Modal>
  );
}

function TriageDetailModal({
  DesignSystemComponents,
  detail,
  loading,
  onClose,
  t,
  onResolveDispatch,
  onAssignCase,
  onDismiss,
  onViewAttachment,
}) {
  const { Modal, Button, IconButton } = DesignSystemComponents ?? {};
  if (!detail && !loading) return null;

  const isPending = detail?.status === 'pending';

  const body = (
    <div>
      {loading || !detail ? (
        <p className='font-light'>{t('triage_sending')}</p>
      ) : (
        <>
          <div className='row mb-2' style={{ fontSize: 13 }}>
            <div className='col-6'>
              <strong>{t('triage_detail_from')}:</strong>{' '}
              {detail.fromIdentifier || '—'}
            </div>
            <div className='col-3'>
              <strong>{t('triage_detail_channel')}:</strong> {detail.channel}
            </div>
            <div className='col-3'>
              <strong>{t('triage_detail_received')}:</strong>{' '}
              {formatDateTime(detail.receivedAt)}
            </div>
          </div>
          <div className='mb-2' style={{ fontSize: 13 }}>
            <strong>{t('triage_detail_subject')}:</strong>{' '}
            {detail.subject || '—'}
          </div>

          <label
            className='form-label fw-semibold mt-2'
            style={{ fontSize: 13 }}
          >
            {t('triage_detail_body')}
          </label>
          <div
            style={{
              border: '1px solid var(--neutral-200, #E2E8F0)',
              borderRadius: 6,
              padding: 10,
              maxHeight: 180,
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              fontSize: 12,
              background: 'var(--neutral-50, #F8FAFC)',
            }}
          >
            {detail.bodyText?.trim() || t('triage_detail_body_empty')}
          </div>

          <label
            className='form-label fw-semibold mt-3'
            style={{ fontSize: 13 }}
          >
            {t('triage_detail_attachments')}
          </label>
          {detail.attachments?.length ? (
            <ul className='mb-2' style={{ fontSize: 13, paddingLeft: 18 }}>
              {detail.attachments.map((att, index) => (
                <li key={`${att.fileName}-${index}`} className='mb-1'>
                  {att.fileName || `adjunto-${index + 1}`}
                  {att.sizeBytes
                    ? ` (${Math.round(att.sizeBytes / 1024)} KB)`
                    : ''}
                  {att.s3Key ? (
                    IconButton ? (
                      <IconButton
                        ariaLabel={t('triage_view_file')}
                        icon='eye'
                        size='md'
                        stroke='var(--primary-500)'
                        onClick={() => onViewAttachment(index, att.fileName)}
                      />
                    ) : (
                      <button
                        type='button'
                        className='btn btn-link btn-sm'
                        onClick={() => onViewAttachment(index, att.fileName)}
                      >
                        {t('triage_view_file')}
                      </button>
                    )
                  ) : (
                    <span className='font-light' style={{ color: '#B45309' }}>
                      {' '}
                      — {t('triage_detail_attachment_no_s3')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className='font-light' style={{ fontSize: 13 }}>
              {t('triage_detail_attachments_empty')}
            </p>
          )}

          {detail.source === 'notary-reply' && (
            <>
              <label
                className='form-label fw-semibold mt-2'
                style={{ fontSize: 13 }}
              >
                {t('triage_detail_candidates')}
              </label>
              {detail.candidates?.length ? (
                <ul style={{ fontSize: 13, paddingLeft: 18 }}>
                  {detail.candidates.map((c) => (
                    <li key={c.dispatchId}>
                      {t('triage_detail_candidate_line', {
                        client:
                          c.clientName || t('triage_resolve_unknown_client'),
                        notary: c.notaryName || '—',
                        sentAt: formatDateTime(c.sentAt),
                      })}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='font-light' style={{ fontSize: 13 }}>
                  {t('triage_detail_candidates_empty')}
                </p>
              )}
            </>
          )}

          {!isPending && (
            <div className='mt-3' style={{ fontSize: 13 }}>
              <strong>{t('triage_detail_resolution')}:</strong>{' '}
              {detail.status === 'resolved_dispatch' &&
                t('triage_detail_resolved_dispatch', {
                  dispatchId: detail.resolvedDispatchId,
                  outcome: detail.resolvedOutcome,
                })}
              {detail.status === 'resolved_case' &&
                t('triage_detail_resolved_case', {
                  caseId: detail.resolvedCaseId,
                })}
              {detail.status === 'dismissed' &&
                t('triage_detail_dismissed', {
                  reason: detail.dismissReason || '—',
                })}
            </div>
          )}

          {isPending && (
            <div className='d-flex justify-content-end align-items-center gap-3 mt-4'>
              <button
                type='button'
                className='btn btn-link btn-sm p-0'
                style={{
                  color: 'var(--error-500, #DC2626)',
                  textDecoration: 'none',
                  fontSize: 13,
                }}
                onClick={onDismiss}
              >
                {t('triage_btn_dismiss')}
              </button>
              {Button ? (
                <>
                  <Button
                    label={t('triage_btn_assign_case')}
                    size='sm'
                    variant='secondary'
                    onClick={onAssignCase}
                  />
                  <Button
                    label={t('triage_btn_resolve_dispatch')}
                    size='sm'
                    variant='primary'
                    onClick={onResolveDispatch}
                  />
                </>
              ) : (
                <>
                  <button
                    type='button'
                    className='btn btn-outline-primary btn-sm'
                    onClick={onAssignCase}
                  >
                    {t('triage_btn_assign_case')}
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary btn-sm'
                    onClick={onResolveDispatch}
                  >
                    {t('triage_btn_resolve_dispatch')}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  const title = detail
    ? t('triage_detail_title', { id: detail.id })
    : t('triage_sending');

  if (!Modal) {
    return (
      <div
        role='dialog'
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,23,42,0.55)',
          zIndex: 9000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: 8,
            padding: 20,
            width: 'min(720px, 92vw)',
            maxHeight: '85vh',
            overflowY: 'auto',
          }}
        >
          <h5>{title}</h5>
          {body}
        </div>
      </div>
    );
  }
  return (
    <Modal title={title} show onClose={onClose} size='lg'>
      {body}
    </Modal>
  );
}

export default function EscriturasTriage({ DesignSystemComponents }) {
  const { language } = useLanguage();
  const triageLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: escriturasTriageI18n });
  const { showSnackbar } = useSnackbar();

  const { Table, Badge, IconButton, Modal, Dropdown, Card } =
    DesignSystemComponents ?? {};

  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const [preview, setPreview] = useState(null); // { url, fileName }
  const [actionModal, setActionModal] = useState(null); // 'resolve' | 'assign' | 'dismiss'

  const abortRef = useRef(null);

  useEffect(() => {
    escriturasTriageI18n.changeLanguage(triageLanguage);
  }, [triageLanguage]);

  const fetchList = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await getTriageList(
        { status: statusFilter, take: pageSize, skip: page * pageSize },
        controller.signal
      );
      setRows(data?.items || []);
      setTotal(data?.total || 0);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('triage_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize, t]);

  useEffect(() => {
    fetchList();
    return () => abortRef.current?.abort();
  }, [fetchList]);

  const openDetail = useCallback(
    async (row) => {
      setDetailOpen(true);
      setDetailLoading(true);
      try {
        const data = await getTriageItem(row.id);
        setDetail(data);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            t('triage_error_generic')
        );
        setDetailOpen(false);
      } finally {
        setDetailLoading(false);
      }
    },
    [t]
  );

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetail(null);
    setActionModal(null);
  }, []);

  const handleViewAttachment = useCallback(
    async (index, fileName) => {
      if (!detail) return;
      try {
        const url = await getTriageAttachmentViewUrl(detail.id, index);
        if (url)
          setPreview({ url, fileName: fileName || `adjunto-${index + 1}` });
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            t('triage_error_generic')
        );
      }
    },
    [detail, t]
  );

  const handleResolved = useCallback(
    (message) => {
      showSnackbar?.(message || t('triage_success_generic'), 'success');
      setActionModal(null);
      closeDetail();
      fetchList();
    },
    [showSnackbar, t, closeDetail, fetchList]
  );

  const statusOptions = useMemo(
    () =>
      STATUS_VALUES.map((value) => ({
        label: t(`triage_status_${value}`),
        value,
      })),
    [t]
  );
  const selectedStatusOption =
    statusOptions.find((o) => o.value === statusFilter) || statusOptions[0];

  const statusBadge = (status) => {
    const color =
      status === 'pending'
        ? 'warning'
        : status === 'dismissed'
          ? 'light'
          : 'success';
    return Badge ? (
      <Badge text={t(`triage_status_${status}`)} color={color} size='sm' />
    ) : (
      <span>{t(`triage_status_${status}`)}</span>
    );
  };

  const sourceBadge = (source) => {
    const key =
      source === 'notary-reply'
        ? 'triage_source_notary_reply'
        : 'triage_source_unknown_sender';
    return Badge ? (
      <Badge
        text={t(key)}
        color={source === 'notary-reply' ? 'primary' : 'light'}
        size='sm'
      />
    ) : (
      <span>{t(key)}</span>
    );
  };

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('triage_title')}</h2>
          <h4 className='fw-normal'>{t('triage_subtitle')}</h4>
        </div>
        {IconButton && (
          <IconButton
            ariaLabel={t('triage_refresh')}
            icon='arrowCounterclockwise'
            size='lg'
            iconFill='var(--primary-500)'
            onClick={fetchList}
            disabled={loading}
          />
        )}
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        <div className='row mb-3'>
          <div className='col-12 col-md-3'>
            {Dropdown ? (
              <Dropdown
                options={statusOptions}
                value={selectedStatusOption}
                onChange={(option) => {
                  setStatusFilter(option?.value || 'pending');
                  setPage(0);
                }}
                placeholder={t('triage_filter_status')}
                width='100%'
                isDisabled={loading}
              />
            ) : (
              <select
                className='form-select form-select-sm'
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                {statusOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className='table-container na-eepp-grid-8'>
          {Table ? (
            <Table
              columns={[
                {
                  accessor: 'receivedAt',
                  label: t('triage_col_received'),
                  render: (row) => formatDateTime(row.receivedAt),
                },
                { accessor: 'channel', label: t('triage_col_channel') },
                { accessor: 'fromIdentifier', label: t('triage_col_from') },
                { accessor: 'subject', label: t('triage_col_subject') },
                {
                  accessor: 'attachmentCount',
                  label: t('triage_col_attachments'),
                },
                {
                  accessor: 'source',
                  label: t('triage_col_source'),
                  render: (row) => sourceBadge(row.source),
                },
                {
                  accessor: 'status',
                  label: t('triage_col_status'),
                  render: (row) => statusBadge(row.status),
                },
                {
                  label: t('triage_col_actions'),
                  render: (row) =>
                    IconButton ? (
                      <IconButton
                        ariaLabel={t('triage_action_view')}
                        icon='eye'
                        size='md'
                        iconStroke='var(--primary-500)'
                        onClick={() => openDetail(row)}
                      />
                    ) : (
                      <button
                        type='button'
                        className='btn btn-outline-primary btn-sm'
                        onClick={() => openDetail(row)}
                      >
                        {t('triage_action_view')}
                      </button>
                    ),
                },
              ]}
              data={rows}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              paginationMode='server'
              totalItems={total}
              paginator
              paginatorGroup={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
              ]}
              loading={loading}
            />
          ) : (
            <p className='font-light'>{loading ? t('triage_sending') : null}</p>
          )}
          {!loading && rows.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>{t('triage_no_results')}</p>
            </div>
          )}
        </div>

        {detailOpen && (
          <TriageDetailModal
            DesignSystemComponents={DesignSystemComponents}
            detail={detail}
            loading={detailLoading}
            onClose={closeDetail}
            t={t}
            onResolveDispatch={() => setActionModal('resolve')}
            onAssignCase={() => setActionModal('assign')}
            onDismiss={() => setActionModal('dismiss')}
            onViewAttachment={handleViewAttachment}
          />
        )}

        {preview && (
          <FilePreviewModal
            Modal={Modal}
            fileUrl={preview.url}
            fileName={preview.fileName}
            onClose={() => setPreview(null)}
          />
        )}

        {actionModal === 'resolve' && detail && (
          <TriageResolveDispatchModal
            DesignSystemComponents={DesignSystemComponents}
            detail={detail}
            t={t}
            onClose={() => setActionModal(null)}
            onResolved={handleResolved}
            onSwitchToDismiss={() => setActionModal('dismiss')}
          />
        )}
        {actionModal === 'assign' && detail && (
          <TriageAssignCaseModal
            DesignSystemComponents={DesignSystemComponents}
            detail={detail}
            t={t}
            onClose={() => setActionModal(null)}
            onResolved={handleResolved}
          />
        )}
        {actionModal === 'dismiss' && detail && (
          <TriageDismissModal
            DesignSystemComponents={DesignSystemComponents}
            detail={detail}
            t={t}
            onClose={() => setActionModal(null)}
            onResolved={handleResolved}
          />
        )}
      </ContentWrapper>
    </div>
  );
}
